import { useState, useEffect, FormEvent } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { authAPI, tokenManager, InviteRole } from '../lib/api'
import { homePathFor } from '../lib/postLoginRedirect'
import { useUser } from '../contexts/UserContext'
import {
  Button, Card, Checkbox, Input, Logo,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  useToast,
} from '../components/ui'

type VerifyState = 'checking' | 'valid' | 'invalid'

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { redeemInvite } = useUser()

  const [verifyState, setVerifyState] = useState<VerifyState>('checking')
  const [officeName, setOfficeName] = useState('')
  const [availableRoles, setAvailableRoles] = useState<InviteRole[]>([])

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [cuiAttested, setCuiAttested] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (tokenManager.isAuthenticated()) {
      // Already signed in — `/` routes them home by role.
      navigate('/', { replace: true })
      return
    }
    if (!token) {
      setVerifyState('invalid')
      return
    }
    authAPI.verifyInvite(token).then((result) => {
      if (result.valid) {
        setOfficeName(result.office_name || '')
        setAvailableRoles(result.available_roles || [])
        setVerifyState('valid')
      } else {
        setVerifyState('invalid')
      }
    })
  }, [token, navigate])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (!role) {
      setError('Please select a role.')
      return
    }
    if (!cuiAttested) {
      setError('Please confirm the CUI attestation.')
      return
    }

    setSubmitting(true)
    try {
      const user = await redeemInvite({
        token: token!,
        password,
        email,
        first_name: firstName,
        last_name: lastName,
        role,
        cui_attestation: cuiAttested,
      })
      addToast({
        variant: 'warning',
        title: 'Verify your email',
        description: `We sent a confirmation link to ${email}. Please check your inbox to verify your address.`,
        duration: 8000,
      })
      navigate(homePathFor(user))
    } catch (err: any) {
      setError(err.message || 'Registration failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center p-4"
      style={{
        backgroundImage: 'url(/ISS_earth.avif)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

      <Card className="relative z-10 w-full max-w-md gap-6 p-6 shadow-2xl">
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo className="mx-auto h-7 w-[231px] max-w-full" />
          <h1 className="text-base font-normal text-muted-foreground">
            Create your Overmatch account
          </h1>
        </div>

        {verifyState === 'checking' && (
          <p className="text-center text-sm text-muted-foreground">Checking your invite&hellip;</p>
        )}

        {verifyState === 'invalid' && (
          <div className="flex flex-col items-center gap-3 text-center">
            <div
              role="alert"
              className="w-full rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              This invite is invalid or has expired.
            </div>
            <Link
              to="/"
              className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Back to login
            </Link>
          </div>
        )}

        {verifyState === 'valid' && (
          <>
            <p className="text-center text-sm text-muted-foreground">
              You've been invited to join <span className="font-medium text-foreground">{officeName}</span>.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    id="invite-first-name"
                    label="First name"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    disabled={submitting}
                  />
                  <Input
                    id="invite-last-name"
                    label="Last name"
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    disabled={submitting}
                  />
                </div>
                <Input
                  id="invite-email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                />
                <div>
                  <label htmlFor="invite-role" className="mb-1 block text-sm font-medium text-foreground">
                    Role
                  </label>
                  <Select value={role} onValueChange={setRole} disabled={submitting}>
                    <SelectTrigger id="invite-role">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((r) => (
                        <SelectItem key={r.codename} value={r.codename}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  id="invite-password"
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={submitting}
                />
                <Input
                  id="invite-confirm-password"
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>

              <Checkbox
                checked={cuiAttested}
                onChange={(e) => setCuiAttested(e.target.checked)}
                label="I attest that this email address is authorized to receive Controlled Unclassified Information (CUI)."
              />

              {error && (
                <div
                  role="alert"
                  className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="h-11 w-full text-base"
                isLoading={submitting}
                disabled={submitting || !cuiAttested}
              >
                {submitting ? 'Creating account...' : 'Create account'}
              </Button>
            </form>
          </>
        )}

        <p className="text-center text-xs text-muted-foreground">
          DoW Acquisitions &amp; Vendor Evaluation Platform
        </p>
      </Card>
    </div>
  )
}
